"use client";

import { T } from "@/lib/i18n/T";
import { DASH_MENU } from "./dashboardData";

/**
 * Dashboard rail — faithful to the old `rubyn__dashboard-container-l`.
 *
 * Section switching is in-memory (state in the parent), NOT route navigation:
 * a click is a `setState`, so there is no RSC fetch / re-mount / re-translate
 * on every menu change — instant, like the old SPA (and snappy in the
 * Capacitor app).
 *
 * Responsive behaviour (recovered from the legacy width/DELTA logic, now CSS +
 * the active section instead of a JS width hook):
 *   • desktop → full vertical menu + Sign out (always);
 *   • phone, on the root  → the menu as a wrapped row of tiles;
 *   • phone, on a section → just a "← Dashboard" back button.
 * `data-root` drives which of the two phone states shows.
 */
export function DashboardRail({
  active,
  onSelect,
}: {
  active: string;
  onSelect: (key: string) => void;
}) {
  const isRoot = active === "dashboard";

  return (
    <nav className="rubyn__dashboard-container-l csx3-C00" data-root={isRoot} aria-label="Account">
      {/* Phone-only: back to the dashboard root (hidden at the root + on desktop). */}
      <button
        type="button"
        className="rubyn__dashboard-container-l__back csx3-R01"
        onClick={() => onSelect("dashboard")}
      >
        <svg width="16" height="10" viewBox="0 0 16 10" fill="currentColor" aria-hidden="true">
          <path d="M0.000976562 4.83203L4.2959 0L5.04395 0.664062L1.7832 4.33203L15.0547 4.33203V5.33203L1.7832 5.33203L5.04395 9L4.2959 9.66406L0.000976562 4.83203Z" />
        </svg>
        <h5><T id="dashboard.menu.dashboard" /></h5>
      </button>

      <div className="rubyn__dashboard-menu csx3-C00">
        {DASH_MENU.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`rubyn__dashboard-container-l__el csx3-R01 ${active === item.key ? "active" : ""}`}
            aria-current={active === item.key ? "page" : undefined}
            onClick={() => onSelect(item.key)}
          >
            {item.icon}
            <h5><T id={item.titleId} /></h5>
          </button>
        ))}
      </div>

      {/* Desktop-only: sign out (the old rail showed this only above DELTA). */}
      <button className="rubyn__dashboard-container-l__el rubyn__dashboard-signout csx3-R01" type="button">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5.55642 14.7404C6.18802 16.2258 7.31347 17.4473 8.74226 18.1982C10.171 18.949 11.8154 19.1831 13.397 18.8609C14.9786 18.5386 16.4002 17.6797 17.4212 16.4296C18.4423 15.1795 19 13.615 19 12.0009C19 10.3868 18.4423 8.82234 17.4212 7.57225C16.4002 6.32216 14.9786 5.46328 13.397 5.14099C11.8154 4.8187 10.171 5.05281 8.74226 5.80368C7.31347 6.55455 6.18802 7.77606 5.55642 9.26143M12.3053 14.436L14.7404 12.0009M14.7404 12.0009L12.3053 9.56582M14.7404 12.0009H5"
            stroke="currentColor"
            strokeWidth="1.21755"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h5><T id="dashboard.signOut" /></h5>
      </button>
    </nav>
  );
}
